import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import {
    Typography,
    FormControl,
    FormHelperText,
    InputLabel,
    InputAdornment,
    IconButton,
    CircularProgress,
    Backdrop,
} from '@mui/material';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import {
    InfoText,
    StepWrapper,
    StyledCardSection,
    DatePicker,
    StepperControls,
    TextField,
} from '@fnol/library/src/components';
import { useApi } from '@fnol/library/src/hooks';

import {
    GenericObject,
    checkUCN,
    common,
    fiveCalendarYearsFromNow,
    prepareCitizenshipsList,
    prepareCountriesList,
    getUrlParameter,
} from '@fnol/library/src/lib';

import {
    translations,
    policiesProperties,
    riskType,
    policyNoMaxLength,
    offerNoMaxLength,
    ucnMaxLength,
    personType,
    riskTypeLife,
} from '../../lib/consts';
import {
    getCities,
    getCitizenships,
    getContractorData,
    getCountries,
    getPoliciesList,
    getPoliciesListForPortal,
    getPolicyDetails,
} from '../../rpc/actions';
import { prepareCitiesList } from '../../lib/helpers';
import { IndividualCleanup } from '../Individual/Individual';
import { ActivePoliciesSelect } from './ActivePoliciesSelect';
import SelectRisk from '../SelectRisk';
import './policy.css';
import { Console } from 'console';

type PolicyDetailScreenProps = {
    navigator: GenericObject[];
    values: GenericObject[];
};

interface FormData {
    policyNumber: string;
    offerNumber: string;
    insuredPersonUCN: string;
    contractorUCN: string;
    policyNumberOfferNumberOrUCN: string;
    dateOfOccurance: string;
    activePolicies: GenericObject[];
    activePolicyNumber: '';
    activePolicy: GenericObject;
    policyNotFound: string;
    risk: riskType;
    risks: number[];
    riskName: string;
    insuredPerson: string;
    countries: Array<GenericObject>;
    cities: Array<GenericObject>;
    usersFromTable: GenericObject[];
    selectedRisks: GenericObject[];
    contractorData: GenericObject;
}

const PolicyDetailScreen = ({ navigator }: PolicyDetailScreenProps) => {
    const [currentStep, { goForward }] = navigator;
    const { values, setFieldValue, errors, setErrors, touched, setTouched } = useFormikContext<FormData>();

    const [showPolicyParams, setShowPolicyParams] = useState<boolean>(false);

    const toDate = (date: string) => {
        return new Date(date);
    };

    let dataForDetails = {};

    let dataForList = {};
    const [riskNote, setRiskNote] = useState<string>('');

    const [preparedCountries, setPreparedCountries] = useState<GenericObject>();
    const saveCitizenships = (res: GenericObject) => {
        setFieldValue('citizenships', prepareCitizenshipsList(res.data));
    };
    const saveCountries = (res: GenericObject) => {
        setFieldValue('countries', prepareCountriesList(res.data));
    };
    const saveCities = (res: GenericObject) => {
        setFieldValue('cities', prepareCitiesList(res.data));
    };
    const saveContractorData = (res: GenericObject) => {
        setFieldValue('contractorData', res.data);
    };

    const { response: citizenships, fetchMethod: fetchCitizenships } = useApi(
        () => getCitizenships(),
        saveCitizenships,
    );
    const { response: countries, fetchMethod: fetchCountries } = useApi(() => getCountries(), saveCountries);
    const { response: cities, fetchMethod: fetchCities } = useApi(() => getCities(), saveCities);
    const { response: contractorData, fetchMethod: fetchContractorData } = useApi(
        () => getContractorData({ BrojPolise: values.policyNumber ? values.policyNumber : values.activePolicyNumber }),
        saveContractorData,
    );

    const savePolicyDetails = (res: GenericObject) => {
        if (res.data.poruka === '') {
            setFieldValue('policyOwner', res.data.ugovarac);
            setFieldValue('amlURL', res.data.amlURL);
            setFieldValue('ownerIdURL', res.data.obrazacZaIdentifikaciju);
            setFieldValue('reportedClaims', res.data.prijavljeneStete);
            if (res.data.sifraStete) {
                setFieldValue('claimCode', res.data.sifraStete);
            }
            if (res.data.stanjeDuga !== undefined) {
                setFieldValue('debtAmount', res.data.stanjeDuga);
            }
            if (res.data.porez !== undefined) {
                setFieldValue('tax', res.data.porez);
            }
            if (res.data.porezOsnovica !== undefined) {
                setFieldValue('taxBase', res.data.porezOsnovica);
            }
            if (res.data.valuta !== undefined) {
                setFieldValue('currency', res.data.valuta);
            }
            if (res.data.iznosZaIsplatu !== undefined) {
                setFieldValue('paymentAmount', res.data.iznosZaIsplatu);
            }
            if (res.data.stavkeObracuna) {
                setFieldValue('calculationItems', res.data.stavkeObracuna);
            }
            if (res.data.kapitalizovana !== undefined) {
                setFieldValue('capitalized', res.data.kapitalizovana);
            }
            if (res.data.datumKapitalizacije !== undefined) {
                setFieldValue('dateCapitalized', res.data.datumKapitalizacije);
            }
            if (res.data.stavkeValidacije !== undefined) {
                setFieldValue('validationData', res.data.stavkeValidacije);
            }
            if (res.data.otkupnaVrednostGodina !== undefined) {
                setFieldValue('purchaseValuePeriod', res.data.otkupnaVrednostGodina);
            }
            if (res.data.otkupnaVrednost !== undefined) {
                setFieldValue('purchaseAmount', res.data.otkupnaVrednost);
            }
            if (res.data.napomena !== null) {
                setFieldValue('infoMessage', res.data.napomena);
            }
            goForward();
        } else {
            setFieldValue('policyNotFound', res.data[policiesProperties.message]);
        }
    };

    const savePolicyList = (res: GenericObject) => {
        if (!res.data[0][policiesProperties.message] || res.data[0][policiesProperties.message] === '') {
            setFieldValue('activePolicies', res.data);
        } else {
            setFieldValue('policyNotFound', res.data[0][policiesProperties.message]);
        }
    };

    const { response: policyDetails, fetchMethod: fetchPolicyDetails } = useApi(
        () => getPolicyDetails(dataForDetails),
        savePolicyDetails,
    );
    const { response: policiesList, fetchMethod: fetchPoliciesList } = useApi(
        () => getPoliciesList(dataForList),
        savePolicyList,
    );
    const { response: policiesListForPortal, fetchMethod: fetchPoliciesListForPortal } = useApi(
        () => getPoliciesListForPortal(),
        savePolicyList,
    );

    const clearUsersTable = () => {
        values.usersFromTable?.map((user) => {
            IndividualCleanup(personType.user, setFieldValue, user.editId);
        });
    };

    const handleNext = () => {
        setTouched(
            {
                dateOfOccurance: true,
                activePolicyNumber: true,
                policyNumber: true,
                offerNumber: true,
                insuredPersonUCN: true,
                contractorUCN: true,
                risks: true,
            },
            false,
        );
        const existingErrors: GenericObject = {};
        let valid = true;

        if (values.activePolicyNumber === '') {
            if (values.policyNumber === '' && values.offerNumber === '' && values.insuredPersonUCN === '') {
                existingErrors.policyNumberHolderIDOrVAT =
                    translations.validationMessages.POLICY_NUMBER_OFFER_NUMBER_OR_UCN_REQUIRED;
            } else {
                existingErrors.activePolicyNumber = translations.validationMessages.REQUIRED_FIELD;
            }
            valid = false;
        }

        if (values.insuredPersonUCN && !checkUCN(values.insuredPersonUCN.toString())) {
            existingErrors.insuredPersonUCN = translations.validationMessages.UCN_INVALID_FORMAT;
            valid = false;
        }

        if (
            getUrlParameter('idAplikacija') === '4' &&
            values.contractorUCN !== '' &&
            !checkUCN(values.contractorUCN.toString())
        ) {
            existingErrors.contractorUCN = translations.validationMessages.UCN_INVALID_FORMAT;
            valid = false;
        }

        if (values.risks.length === 0) {
            existingErrors.risks = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.dateOfOccurance) {
            existingErrors.dateOfOccurance = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        setErrors(existingErrors);

        if (valid) {
            const insureeId = values.activePolicies.filter(
                (activePolicy) => activePolicy.brojPolise === values.activePolicyNumber,
            )[0].osiguranikId;
            dataForDetails = {
                BrojPolise: values.activePolicyNumber,
                OsiguranikId: insureeId,
                DatumNastanka:
                    values.dateOfOccurance !== ''
                        ? `${toDate(values.dateOfOccurance).getFullYear()}-${(
                              '0' +
                              (toDate(values.dateOfOccurance).getMonth() + 1)
                          ).slice(-2)}-${('0' + toDate(values.dateOfOccurance).getDate()).slice(-2)}`
                        : values.dateOfOccurance,
                RiziciId: values.risks,
            };
            fetchPolicyDetails();
            values.risks.includes(riskTypeLife.purchase) && fetchContractorData();
        }
    };

    const checkPolicyNumberAndGetPoliciesList = () => {
        if (values.policyNumber !== '') {
            setFieldValue('offerNumber', '');
            setFieldValue('insuredPersonUCN', '');
            setFieldValue('contractorUCN', '');
            setFieldValue('activePolicies', []);
            setFieldValue('activePolicyNumber', '');
            setFieldValue('activePolicy', null);
            setFieldValue('risk', '');
            setFieldValue('usersList', []);
            dataForList = {
                BrojPolise: values.policyNumber,
                BrojPonude: '',
                JMBG: '',
                UgovaracJMBG: '',
            };

            clearUsersTable();
            fetchPoliciesList();
        }
        setFieldValue('policyNotFound', '');
    };

    const checkOfferNumberAndGetPoliciesList = () => {
        if (values.offerNumber !== '') {
            setFieldValue('policyNumber', '');
            setFieldValue('insuredPersonUCN', '');
            setFieldValue('contractorUCN', '');
            setFieldValue('activePolicies', []);
            setFieldValue('activePolicyNumber', '');
            setFieldValue('activePolicy', null);
            setFieldValue('risk', '');
            setFieldValue('usersList', []);
            dataForList = {
                BrojPolise: '',
                BrojPonude: values.offerNumber,
                JMBG: '',
                UgovaracJMBG: '',
            };
            clearUsersTable();
            fetchPoliciesList();
        }
        setFieldValue('policyNotFound', '');
    };

    const checkInsuredPersonUCNAndGetPoliciesList = () => {
        if (values.insuredPersonUCN !== '') {
            setFieldValue('policyNumber', '');
            setFieldValue('offerNumber', '');
            setFieldValue('contractorUCN', '');
            setFieldValue('activePolicies', []);
            setFieldValue('activePolicyNumber', '');
            setFieldValue('activePolicy', null);
            setFieldValue('risk', '');
            setFieldValue('usersList', []);
            dataForList = {
                BrojPolise: '',
                BrojPonude: '',
                JMBG: values.insuredPersonUCN.toString(),
                UgovaracJMBG: '',
            };
            clearUsersTable();
            fetchPoliciesList();
        }
        setFieldValue('policyNotFound', '');
    };

    const checkContractorUCNAndGetPoliciesList = () => {
        if (values.contractorUCN !== '') {
            setFieldValue('policyNumber', '');
            setFieldValue('offerNumber', '');
            setFieldValue('insuredPersonUCN', '');
            setFieldValue('activePolicies', []);
            setFieldValue('activePolicyNumber', '');
            setFieldValue('activePolicy', null);
            setFieldValue('risk', '');
            setFieldValue('usersList', []);
            dataForList = {
                BrojPolise: '',
                BrojPonude: '',
                JMBG: '',
                UgovaracJMBG: values.contractorUCN.toString(),
            };
            clearUsersTable();
            fetchPoliciesList();
        }
        setFieldValue('policyNotFound', '');
    };

    const activePolicyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const activePolicy = values.activePolicies.filter((policy: GenericObject) => {
            return policy.brojPolise === e.target.value;
        });

        setFieldValue('activePolicyNumber', e.target.value);
        setFieldValue('activePolicy', activePolicy[0]);
        const validTo = new Date(activePolicy[0][policiesProperties.validTo]);
        setFieldValue('policyValidTo', `${validTo.getDate()}/${validTo.getMonth() + 1}/${validTo.getFullYear()}`);
        setFieldValue('insuredPerson', activePolicy[0][policiesProperties.insuredPerson]);
        setFieldValue('policyNotFound', '');
        setFieldValue('risks', []);
        setFieldValue('infoMessage', '');

        clearUsersTable();
    };

    const dateChange = () => {
        setFieldValue('policyNotFound', '');
    };

    const handleMaxNumbersLength = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.target.value = e.target.value.slice(0, 13);
    };

    const numberInputOnWheelPreventChange = (e: React.WheelEvent<HTMLElement>) => {
        (e.target as HTMLElement).blur();
        e.stopPropagation();
        setTimeout(() => {
            (e.target as HTMLElement).focus();
        }, 0);
    };

    useEffect(() => {
        const guid = localStorage.getItem(`life_guid`);
        const appId = localStorage.getItem(`life_appID`);
        if (guid && guid !== '' && appId && appId === '1') {
            fetchPoliciesListForPortal();
        } else {
            setShowPolicyParams(true);
        }
    }, []);

    useEffect(() => {
        fetchCitizenships();
        fetchCountries();
        fetchCities();
    }, []);

    useEffect(() => {
        Array.isArray(values.countries) && setPreparedCountries(values.countries);
    }, [countries]);

    useEffect(() => {
        if (values.selectedRisks?.length > 0) {
            values.selectedRisks.map((risk) => {
                if (risk.note) {
                    setRiskNote(risk.note);
                    return;
                }
                setRiskNote('');
            });
        }
    }, [values.selectedRisks]);

    return (
        <StepWrapper>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '1.1rem' }} variant="caption">
                    {translations.DATE_OF_OCCURANCE}
                </Typography>
                <FormControl error={!!(touched.dateOfOccurance && errors.dateOfOccurance)} className="policyDatePicker">
                    <Field
                        name="dateOfOccurance"
                        label={translations.DATE}
                        as={DatePicker}
                        disableFuture
                        minDate={fiveCalendarYearsFromNow}
                        error={!!(touched.dateOfOccurance && errors.dateOfOccurance)}
                        onChangeDate={dateChange}
                    />
                    {touched.dateOfOccurance && errors.dateOfOccurance ? (
                        <FormHelperText error={true}>
                            <>{errors.dateOfOccurance}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            </StyledCardSection>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '1.1rem' }} variant="caption">
                    {translations.POLICY}
                </Typography>
                {showPolicyParams && (
                    <>
                        <FormHelperText error={false}>
                            <>{translations.POLICY_INSTRUCTIONS_1}</>
                        </FormHelperText>
                        <FormControl error={!!(touched.policyNumber && errors.policyNumber)}>
                            <Field
                                name="policyNumber"
                                label={translations.POLICY_NUMBER}
                                as={TextField}
                                inputProps={{
                                    maxLength: policyNoMaxLength,
                                }}
                                error={!!(touched.policyNumber && errors.policyNumber)}
                                onBlur={checkPolicyNumberAndGetPoliciesList}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="find policy"
                                                onClick={checkPolicyNumberAndGetPoliciesList}
                                                edge="end"
                                            >
                                                {policiesList.loading ? <CircularProgress /> : <FindInPageIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {touched.policyNumber && errors.policyNumber ? (
                                <FormHelperText error={true}>
                                    <>{errors.policyNumber}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl error={!!(touched.offerNumber && errors.offerNumber)}>
                            <Field
                                name="offerNumber"
                                label={translations.OFFER_NUMBER}
                                as={TextField}
                                inputProps={{
                                    maxLength: offerNoMaxLength,
                                }}
                                error={!!(touched.offerNumber && errors.offerNumber)}
                                onBlur={checkOfferNumberAndGetPoliciesList}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="find policy"
                                                onClick={checkOfferNumberAndGetPoliciesList}
                                                edge="end"
                                            >
                                                {policiesList.loading ? <CircularProgress /> : <FindInPageIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {touched.offerNumber && errors.offerNumber ? (
                                <FormHelperText error={true}>
                                    <>{errors.offerNumber}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl error={!!(touched.insuredPersonUCN && errors.insuredPersonUCN)}>
                            <Field
                                name="insuredPersonUCN"
                                label={translations.INSURED_PERSON_UCN}
                                as={TextField}
                                inputProps={{
                                    maxLength: ucnMaxLength,
                                    min: 0,
                                }}
                                onInput={handleMaxNumbersLength}
                                onWheel={numberInputOnWheelPreventChange}
                                error={!!(touched.insuredPersonUCN && errors.insuredPersonUCN)}
                                onBlur={checkInsuredPersonUCNAndGetPoliciesList}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="find policy"
                                                onClick={checkInsuredPersonUCNAndGetPoliciesList}
                                                edge="end"
                                            >
                                                {policiesList.loading ? <CircularProgress /> : <FindInPageIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {touched.insuredPersonUCN && errors.insuredPersonUCN ? (
                                <FormHelperText error={true}>
                                    <>{errors.insuredPersonUCN}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        {getUrlParameter('idAplikacija') === '4' && (
                            <FormControl error={!!(touched.contractorUCN && errors.contractorUCN)}>
                                <Field
                                    name="contractorUCN"
                                    label={translations.CONTRACTOR_UCN}
                                    as={TextField}
                                    inputProps={{
                                        maxLength: ucnMaxLength,
                                        min: 0,
                                    }}
                                    onInput={handleMaxNumbersLength}
                                    onWheel={numberInputOnWheelPreventChange}
                                    error={!!(touched.contractorUCN && errors.contractorUCN)}
                                    onBlur={checkContractorUCNAndGetPoliciesList}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="find policy"
                                                    onClick={checkContractorUCNAndGetPoliciesList}
                                                    edge="end"
                                                >
                                                    {policiesList.loading ? <CircularProgress /> : <FindInPageIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {touched.contractorUCN && errors.contractorUCN ? (
                                    <FormHelperText error={true}>
                                        <>{errors.contractorUCN}</>
                                    </FormHelperText>
                                ) : null}
                            </FormControl>
                        )}
                        {touched.policyNumber &&
                        touched.offerNumber &&
                        touched.insuredPersonUCN &&
                        errors.policyNumberOfferNumberOrUCN ? (
                            <FormHelperText error={true}>
                                <>{errors.policyNumberOfferNumberOrUCN}</>
                            </FormHelperText>
                        ) : null}
                    </>
                )}
                {values.policyNotFound !== '' && (
                    <FormHelperText error={true}>
                        <>{values.policyNotFound}</>
                    </FormHelperText>
                )}
                {values.activePolicies.length > 0 && (
                    <>
                        <FormHelperText error={false}>
                            <>{translations.POLICY_INSTRUCTIONS_2}</>
                        </FormHelperText>
                        <FormControl error={!!(touched.activePolicyNumber && errors.activePolicyNumber)} required>
                            <InputLabel id="activePolicyNumber">{translations.POLICY_SELECT_LABEL}</InputLabel>
                            <Field
                                name="activePolicyNumber"
                                label={translations.POLICY_SELECT_LABEL}
                                value={values.activePolicyNumber}
                                items={values.activePolicies}
                                onChange={activePolicyChange}
                                as={ActivePoliciesSelect}
                                showAllInfo={!showPolicyParams}
                            />
                            {touched.activePolicyNumber && errors.activePolicyNumber ? (
                                <FormHelperText error={true}>
                                    <>{errors.activePolicyNumber}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </>
                )}
                {values.activePolicy && <SelectRisk></SelectRisk>}
                {riskNote === '' ? (
                    <InfoText label={translations.documentation.NOTICE_NECESSITY_VISITS_BRANCH}></InfoText>
                ) : (
                    <InfoText label={riskNote}></InfoText>
                )}
            </StyledCardSection>
            <StepperControls onNext={handleNext} onNextLabel={common.NEXT} onPreviousLabel={common.PREVIOUS} />
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={policiesList.isLoading}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </StepWrapper>
    );
};

export default PolicyDetailScreen;
